'use client';

/**
 * Global error boundary — Next.js's last-resort UI when an error escapes
 * the root layout or a route-level `error.tsx` itself throws.
 *
 * This is where browser-side unhandled render errors get forwarded to Sentry,
 * the exception system-of-record. PostHog no longer receives errors — it keeps
 * only product analytics + session replay.
 *
 * No-op in local development: `Sentry.getClient()` is undefined until Sentry
 * inits (production + DSN set), so the error just surfaces in the browser
 * DevTools console via the fallback.
 *
 * Must render its own <html><body> because it replaces the entire root
 * layout when invoked.
 */

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import { fontVars } from '@write4me/ui/fonts';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    if (Sentry.getClient()) {
      // Sentry owns browser exceptions. The Next.js `digest` correlates this
      // render error with its server-side counterpart.
      Sentry.captureException(error, { extra: { digest: error.digest } });
    } else {
      // Dev / telemetry-off: still surface the error so the developer sees it.
      console.error('[global-error]', error);
    }
  }, [error]);

  return (
    // UI-06: `fontVars` must be applied here. This boundary renders its own
    // <html>, replacing the root layout that normally sets the font variables
    // — so `var(--font-fraunces)` below resolved to nothing and the heading
    // fell back to the platform serif, off-brand at the worst possible moment.
    <html lang="en" className={fontVars}>
      <body className="antialiased">
        <div className="min-h-screen flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <h1
              className="text-2xl font-medium"
              style={{ fontFamily: 'var(--font-fraunces), serif' }}
            >
              Something went wrong
            </h1>
            <p className="mt-2 text-slate-500">
              We&apos;ve been notified and are looking into it.
            </p>
            {/*
              UI-06: the component always accepted `reset` and never rendered
              anything wired to it, telling the user to refresh by hand
              instead. Plain inline styles — this boundary can render before
              the stylesheet is available.
            */}
            <button
              type="button"
              onClick={() => reset()}
              className="mt-6 inline-flex items-center justify-center rounded-[12px] px-5 py-2.5 text-sm font-medium text-white"
              style={{
                backgroundColor: '#ed5526',
                fontFamily: 'var(--font-inter), sans-serif',
              }}
            >
              Try again
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
